// Mock data for Talas Trading Co clone

export const NAV_ITEMS = [
  { label: 'Features', to: '/features' },
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Risk Disclosure', to: '/risk-disclosure' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export const FEATURE_STRIP = [
  { icon: 'Lock', label: 'ENCRYPTED BROKER\nTOKEN HANDLING' },
  { icon: 'FileCheck', label: 'PAPER MODE\nTESTING' },
  { icon: 'ShieldAlert', label: 'RISK LOCKOUT\nRULES' },
  { icon: 'Activity', label: 'ADMIN MONITORING' },
  { icon: 'ScrollText', label: 'AUDIT LOGS' },
  { icon: 'Power', label: 'EMERGENCY KILL\nSWITCH' },
];

export const OPEN_POSITIONS = [
  { symbol: 'NQ', side: 'LONG', qty: 1, entry: '30,355.75', last: '30,367.00', pnl: '+225.00', positive: true },
  { symbol: 'ES', side: 'SHORT', qty: 2, entry: '7,590.00', last: '7,587.25', pnl: '+275.00', positive: true },
  { symbol: 'CL', side: 'LONG', qty: 1, entry: '88.40', last: '88.58', pnl: '+180.00', positive: true },
];

export const ACTIVITY_LOG = [
  { text: 'Bracket order placed · NQ Long @ 30,355.75', time: '09:32:14' },
  { text: 'Risk check passed · within daily loss budget', time: '09:31:02' },
  { text: 'Broker heartbeat ok · 11 ms latency', time: '09:30:45' },
  { text: 'Session opened · Live mode active', time: '09:30:10' },
];

export const DASHBOARD_STATS = {
  dailyPnl: '+$680.00',
  riskUsed: { percent: 38, used: 190, total: 500 },
};

export const CAPABILITIES = [
  {
    icon: 'Zap',
    version: 'v1.0',
    title: 'Structured Execution Controls',
    description: 'Pre-configured order templates, bracket orders with mandatory stop-loss, position sizing aligned to your account risk profile.',
    points: ['Bracket orders with mandatory SL/TP', 'Per-trade position sizing', 'Symbol-level execution rules'],
  },
  {
    icon: 'Shield',
    version: 'v1.0',
    title: 'Risk-Defined Trade Management',
    description: 'Daily loss limits, max drawdown lockouts, and consecutive-loss circuit breakers enforced server-side before any order routes.',
    points: ['Daily loss budget enforcement', 'Consecutive-loss lockouts', 'Server-side risk validation'],
  },
  {
    icon: 'Wifi',
    version: 'v1.0',
    title: 'Broker-Connected Automation',
    description: 'Direct OAuth connections to Alpaca, Tradovate, and IBKR. Encrypted tokens, replayable audit logs, and graceful disconnect handling.',
    points: ['Alpaca, Tradovate, IBKR support', 'Fernet-encrypted credentials', 'Replayable session audit logs'],
  },
  {
    icon: 'LineChart',
    version: 'v1.0',
    title: 'Paper-Mode Validation Layer',
    description: 'Every new strategy must clear a paper-mode validation window before live execution is unlocked. No exceptions.',
    points: ['Mandatory paper validation', 'Performance gating to live mode', 'Reset & re-validate on rule changes'],
  },
  {
    icon: 'Bell',
    version: 'v1.0',
    title: 'Real-Time System Heartbeat',
    description: 'Continuous broker, data, and bot heartbeat monitoring with sub-second alerts on disconnect or latency spikes.',
    points: ['Broker connection heartbeat', 'Latency monitoring', 'Instant disconnect alerts'],
  },
  {
    icon: 'KeyRound',
    version: 'v1.0',
    title: 'Operator-Grade Access Control',
    description: 'Role-based access, session keys rotated on a schedule, and admin actions logged to an immutable audit trail.',
    points: ['Role-based permissions', 'Rotating session keys', 'Immutable audit trail'],
  },
];

export const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Create Your Account',
    description: 'Register with your email and create a secure password. Verify your email to activate your account.',
    points: ['Email and password registration', 'Secure JWT-based authentication', 'Role-based access control'],
  },
  {
    step: '02',
    title: 'Choose a Subscription Plan',
    description: 'Select the plan that matches your trading needs — Starter for paper trading, Pro or Elite for live automation.',
    points: ['Starter: Paper trading and basic config', 'Pro: Live broker connection and automation', 'Elite: Multi-account and priority support'],
  },
  {
    step: '03',
    title: 'Connect Your Broker',
    description: 'Link your broker account using OAuth or API token. All credentials are encrypted at rest. You can disconnect at any time.',
    points: ['Alpaca, Tradovate, IBKR connectors', 'API tokens encrypted with Fernet', 'Full tokens never displayed to users or admins'],
  },
  {
    step: '04',
    title: 'Configure Bot Settings and Risk Rules',
    description: 'Set your trading parameters — position sizing, daily loss limits, allowed symbols, and trading hours.',
    points: ['Position sizing and risk per trade', 'Daily loss budget and lockouts', 'Symbol allowlist and session windows'],
  },
  {
    step: '05',
    title: 'Validate in Paper Mode',
    description: 'Run your configuration against live market data in paper mode. Live execution unlocks only after validation passes.',
    points: ['Live market data, simulated fills', 'Mandatory validation window', 'One-click promote to live'],
  },
  {
    step: '06',
    title: 'Go Live with Confidence',
    description: 'Promote to live execution. Monitor positions, P&L, and risk metrics in real time — with kill-switch always one click away.',
    points: ['Real-time positions & P&L', 'Always-on kill switch', 'Full session audit logs'],
  },
];

export const PRICING = [
  {
    name: 'Starter',
    packageId: 'starter',
    price: '49.99',
    cadence: '/ month',
    tagline: 'The most accessible way to start with Talas automation. Limited to 5 stock signals per day.',
    features: [
      'Up to 5 stock signals per day',
      'Live broker connection (Alpaca, IBKR)',
      'Bracket orders with mandatory SL/TP',
      'Daily loss budget & lockouts',
      'Paper-mode validation gate',
      'Real-time heartbeat alerts',
      'Audit logs (1 year)',
      'Email support',
    ],
    cta: 'Start with Starter',
    highlight: false,
    badge: 'NEW',
  },
  {
    name: 'Stock Bot',
    packageId: 'stock_bot',
    price: '199',
    cadence: '/ month',
    tagline: 'Hands-off automated trading on equities. Paper-validated, broker-connected.',
    features: [
      'Stock bot only',
      'Live broker connection (Alpaca, IBKR)',
      'Bracket orders with mandatory SL/TP',
      'Daily loss budget & lockouts',
      'Paper-mode validation gate',
      'Real-time heartbeat alerts',
      'Audit logs (1 year)',
      'Email support',
    ],
    cta: 'Start Stock Bot',
    highlight: false,
  },
  {
    name: 'Stock + Options Bot',
    packageId: 'stock_options_bot',
    price: '299',
    cadence: '/ month',
    tagline: 'Full coverage. Both the stock bot and the options bot, running side-by-side.',
    features: [
      'Stock bot + Options bot',
      'Live broker connection (Alpaca, IBKR, Tradovate)',
      'Bracket orders with mandatory SL/TP',
      'Per-strategy risk profiles',
      'Daily loss budget & lockouts',
      'Paper-mode validation gate',
      'Real-time heartbeat alerts',
      'Audit logs (unlimited)',
      'Priority support',
    ],
    cta: 'Start Both Bots',
    highlight: true,
  },
];

export const FAQS = [
  {
    q: 'Does Talas guarantee profits?',
    a: 'No. Automation does not guarantee profits or prevent losses. Beta is designed for paper trading, testing, and transparency before live funds are ever involved.',
  },
  {
    q: 'Do I have to give Talas my Alpaca password?',
    a: 'No. Talas does not ask for or store your Alpaca password. Users connect through Alpaca\u2019s secure OAuth authorization flow. You log in with Alpaca directly, approve access, and Talas receives a secure connection token for your Alpaca Paper account.',
  },
  {
    q: 'Does Talas use live money during beta?',
    a: 'No. Talas beta is paper-trading only. No live funds are used during beta. Live trading access is not enabled for users during beta.',
  },
  {
    q: 'Can Talas place trades without my permission?',
    a: 'Paper auto-trading is off by default. Users must manually enable paper auto-trading before Talas can place paper trades in their connected Alpaca Paper account.',
  },
  {
    q: 'Can I disconnect Alpaca?',
    a: 'Yes. Users can disconnect their Alpaca Paper account from the dashboard. Disconnecting removes Talas access to that connected paper account.',
  },
  {
    q: 'What if I connect the wrong Alpaca account?',
    a: 'Disconnect Alpaca from your dashboard, make sure you are logged into the correct Alpaca account in your browser, then reconnect. Talas connects to whichever Alpaca account you authorize through Alpaca.',
  },
  {
    q: 'How does Talas size trades for different users?',
    a: 'Talas uses percentage-based sizing. If a signal uses 3%, each connected user\u2019s paper trade is sized from their own Alpaca Paper account equity, not from another user\u2019s account size.\n\nExample:\nA $1,000 paper account at 3% \u2248 $30.\nA $10,000 paper account at 3% \u2248 $300.',
  },
  {
    q: 'How are my broker credentials stored?',
    a: 'OAuth tokens issued by Alpaca are encrypted at rest using Fernet symmetric encryption. Talas never sees or stores your Alpaca password \u2014 only a revocable access token tied to your Alpaca Paper account.',
  },
  {
    q: 'What happens if my broker disconnects?',
    a: 'If the Alpaca Paper connection fails or disconnects, Talas stops execution attempts for that user and shows the connection status in the dashboard. The user can reconnect from their dashboard.',
  },
  {
    q: 'How do I stop everything immediately?',
    a: 'Users can turn off paper auto-trading, disconnect Alpaca, or use available dashboard controls. Admin-side safety controls and risk lockout rules also exist during beta.',
  },
  {
    q: 'Is Talas financial advice?',
    a: 'No. Talas provides software, automation tools, signal tracking, and paper-trading workflows. Talas does not provide individualized financial advice. Users should understand the risks before trading.',
  },
  {
    q: 'What do I need to start?',
    a: 'You need a Talas account and an Alpaca Paper account. During beta, users connect Alpaca Paper only. No live brokerage funds are required.',
  },
  {
    q: 'Is beta free?',
    a: 'Yes. Beta access is currently free. Future paid plans may be added later, but beta users will be clearly notified before any billing changes.',
  },
];

export const RISK_DISCLOSURE_POINTS = [
  'Trading futures, equities, and derivatives involves substantial risk and is not suitable for every investor. You may lose all or more than your initial investment.',
  'Automation does not eliminate market risk. Connectivity, broker, exchange, data, and platform failures can occur and may result in losses, missed fills, or unintended executions.',
  'Paper-mode results are simulated. Simulated performance does not represent actual trading and does not guarantee future live results.',
  'You remain responsible for the strategies, parameters, risk rules, and broker credentials you configure on the Talas platform.',
  'Talas Trading Co. is a software and infrastructure provider. We do not provide personalized investment advice, manage discretionary accounts, or guarantee any outcome.',
];

export const LOGO_URL = '/assets/talas-logo.png';
